import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { requestCommentsService } from "@/services/requestCommentsService";
import {
  CreateRequestCommentDto,
  RequestCommentsFilters,
  UpdateRequestCommentDto,
} from "@/types/requestComment";

export const requestCommentKeys = {
  all: ["request-comments"] as const,
  lists: () => [...requestCommentKeys.all, "list"] as const,
  list: (filters?: RequestCommentsFilters) =>
    [...requestCommentKeys.lists(), filters] as const,
  details: () => [...requestCommentKeys.all, "detail"] as const,
  detail: (id: string) => [...requestCommentKeys.details(), id] as const,
};

export const useRequestComments = (filters?: RequestCommentsFilters) => {
  const enabled = !!filters?.requestId;
  return useQuery({
    queryKey: requestCommentKeys.list(filters),
    queryFn: () => requestCommentsService.getAll(filters!),
    enabled,
  });
};

export const useRequestComment = (id: string) => {
  return useQuery({
    queryKey: requestCommentKeys.detail(id),
    queryFn: () => requestCommentsService.getById(id),
    enabled: !!id,
  });
};

export const useCreateRequestComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRequestCommentDto) =>
      requestCommentsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: requestCommentKeys.lists(),
      });
    },
  });
};

export const useUpdateRequestComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRequestCommentDto }) =>
      requestCommentsService.update(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(requestCommentKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: requestCommentKeys.lists() });
    },
  });
};

export const useDeleteRequestComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => requestCommentsService.deleteComment(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({
        queryKey: requestCommentKeys.detail(deletedId),
      });
      queryClient.invalidateQueries({ queryKey: requestCommentKeys.lists() });
    },
  });
};
